import React, { useState, useEffect, useCallback } from 'react';
import { 
    Box, Card, CardContent, Typography, TextField, Button, Alert,
    Container, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
    Stepper, Step, StepLabel, CircularProgress, Tabs, Tab, InputAdornment, IconButton,
    FormControlLabel, Checkbox
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LoginIcon from '@mui/icons-material/Login';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import GoogleIcon from '@mui/icons-material/Google';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useBranding } from '../context/BrandingContext';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import LionIcon from '../components/LionIcon';
import PoweredByBadge from '../components/PoweredByBadge';
import api from '../api/axios';
import { trackEvent } from '../utils/gtm';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Detect iOS / Android once at module level (stable — userAgent never changes)
const IS_MOBILE_DEVICE = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// Detect if running as an installed PWA (standalone mode)
const IS_PWA = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true; // iOS Safari

// Use redirect mode on ALL mobile devices (Chrome mobile, Safari, PWA).
// Popup mode on mobile is unreliable: Chrome opens a new tab and the callback
// never fires back to the original page / PWA window.
const USE_REDIRECT_MODE = IS_MOBILE_DEVICE || IS_PWA;

// Canonical origin for Google redirect (must match Google Cloud Console exactly).
// Prefer REACT_APP_APP_URL; on production hosts always use https://emaillion.net
// so www / http variants don't trigger redirect_uri_mismatch.
function getGoogleRedirectOrigin() {
    const fromEnv = (process.env.REACT_APP_APP_URL || '').trim().replace(/\/+$/, '');
    if (fromEnv) return fromEnv;
    try {
        const { hostname, origin } = window.location;
        if (/^(www\.)?emaillion\.net$/i.test(hostname)) return 'https://emaillion.net';
        return origin;
    } catch {
        return 'https://emaillion.net';
    }
}
const GOOGLE_REDIRECT_URI = `${getGoogleRedirectOrigin()}/api/auth/google/callback`;

const LoginPage = () => {
    const [searchParams] = useSearchParams();
    const { t, isRtl, lang } = useLanguage();
    const initialTab = searchParams.get('tab') === 'register' ? 1 : 0;
    const [tab, setTab] = useState(initialTab); // 0 = login, 1 = register
    const sessionExpired = searchParams.get('expired') === '1';
    const referralCode = searchParams.get('ref') || '';
    const wantDemo = searchParams.get('demo') === '1';
    // Safe internal return path only (blocks open redirects)
    const rawReturn = searchParams.get('return') || searchParams.get('redirect') || '';
    const safeReturn = (rawReturn.startsWith('/') && !rawReturn.startsWith('//')) ? rawReturn : null;
    const postAuthPath = () => {
        if (safeReturn) return safeReturn;
        if (wantDemo) return '/webmail?connect=1&demo=1';
        return null;
    };
    const [error, setError] = useState(sessionExpired ? t('login.sessionExpired') : '');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [gsiReady, setGsiReady] = useState(false);
    const { login, user, setUser } = useAuth();
    const { branding, loading: brandingLoading } = useBranding();
    const isWhiteLabel = branding?.whitelabel;
    const navigate = useNavigate();

    // Login state
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');

    // Register state
    const [regName, setRegName] = useState('');
    const [regEmail, setRegEmail] = useState('');
    const [regPassword, setRegPassword] = useState('');
    const [regConfirm, setRegConfirm] = useState('');
    const [regCompany, setRegCompany] = useState('');
    const [regPhone, setRegPhone] = useState('');

    // 2FA state
    const [show2FA, setShow2FA] = useState(false);
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const [rememberMe, setRememberMe] = useState(true);
    // "Stay signed in forever on this device" – like Gmail's mobile app.
    // When enabled the refresh token is issued for ~10 years instead of 30 days.
    const [rememberForever, setRememberForever] = useState(false);

    // Forgot password state
    const [forgotOpen, setForgotOpen] = useState(false);
    const [forgotStep, setForgotStep] = useState(0);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotCode, setForgotCode] = useState('');
    const [forgotNewPass, setForgotNewPass] = useState('');
    const [forgotConfirm, setForgotConfirm] = useState('');
    const [forgotMessage, setForgotMessage] = useState('');
    const [forgotError, setForgotError] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);

    // ==================== LOGIN HANDLER ====================
    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // "Forever" implies rememberMe is also on. Translate the two checkboxes
        // into a single rememberMode that the server understands.
        const mode = rememberForever ? 'forever' : (rememberMe ? 'extended' : null);
        const result = await login(loginEmail, loginPassword, show2FA ? twoFactorCode : undefined, rememberMe || rememberForever, mode);
        setLoading(false);

        if (result.success) {
            const dest = postAuthPath()
                || (result.user?.role === 'user' ? '/webmail' : '/dashboard');
            navigate(dest);
        } else if (result.requires2FA) {
            setShow2FA(true);
        } else {
            setError(result.message);
        }
    };

    // ==================== REGISTER HANDLER ====================
    const handleRegister = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!regName.trim()) return setError(t('login.errorFullName'));
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(regEmail.trim());
        if (!regEmail.trim() || !emailOk) return setError(t('login.errorValidEmail'));
        const phoneDigits = (regPhone || '').replace(/\D/g, '');
        if (!regPhone.trim() || phoneDigits.length < 9) return setError(t('login.errorPhone'));
        if (regPassword.length < 6) return setError(t('login.errorMinPassword'));
        if (regPassword !== regConfirm) return setError(t('login.errorPasswordMatch'));

        setLoading(true);
        try {
            const res = await api.post('/auth/register', {
                fullName: regName,
                email: regEmail.trim(),
                password: regPassword,
                orgName: regCompany || undefined,
                phone: regPhone.trim(),
                referralCode: referralCode || undefined,
            });

            // Client-side track as backup (server also binds on register)
            if (referralCode && res.data?.user?.id) {
                try {
                    await api.post('/sales/referral/track', {
                        code: referralCode,
                        email: regEmail,
                        userId: res.data.user.id,
                    });
                } catch { /* ignore */ }
            }

            if (res.data.token && res.data.user) {
                localStorage.setItem('token', res.data.token);
                localStorage.setItem('user', JSON.stringify(res.data.user));
                if (setUser) setUser(res.data.user);
                trackEvent('sign_up', { method: 'email', source: 'login_page' });
                // New signups go straight to connect-mailbox flow (activation), not empty dashboard
                navigate(postAuthPath() || (wantDemo ? '/webmail?connect=1&demo=1' : '/webmail?connect=1'));
            } else {
                setSuccess(t('login.registerSuccess'));
                setTab(0);
                setLoginEmail(regEmail);
            }
        } catch (err) {
            setError(err.response?.data?.message || t('login.errorRegister'));
        } finally {
            setLoading(false);
        }
    };

    // ==================== GOOGLE AUTH ====================
    const handleGoogleResponse = useCallback(async (response) => {
        if (!response.credential) return;
        setError('');
        setLoading(true);

        try {
            const res = await api.post('/auth/google', { credential: response.credential });
            if (res.data.token && res.data.user) {
                localStorage.setItem('token', res.data.token);
                localStorage.setItem('user', JSON.stringify(res.data.user));
                if (setUser) setUser(res.data.user);
                if (res.data.isNewUser) {
                    trackEvent('sign_up', { method: 'google', source: 'login_page' });
                }
                const override = postAuthPath();
                const dest = override
                    || (res.data.isNewUser || res.data.user?.role === 'user'
                        ? '/webmail?connect=1'
                        : (res.data.user.role === 'user' ? '/webmail' : '/dashboard'));
                navigate(dest);
            }
        } catch (err) {
            setError(err.response?.data?.message || t('login.errorGoogleAuth'));
        } finally {
            setLoading(false);
        }
    }, [navigate, setUser, t, safeReturn, wantDemo]);

    // Handle google_error query param from redirect callback failures
    useEffect(() => {
        const errParam = searchParams.get('google_error');
        if (errParam) setError(t('login.errorGoogleFailed'));
    }, [searchParams, t]);

    // Load Google Identity Services script (skip on white label domains)
    useEffect(() => {
        if (!GOOGLE_CLIENT_ID || brandingLoading || isWhiteLabel) return;

        const existingScript = document.getElementById('google-gsi-script');
        if (existingScript) {
            // Script was already loaded (e.g. hot-reload), just set ready flag
            if (window.google?.accounts?.id) setGsiReady(true);
            return;
        }

        const script = document.createElement('script');
        script.id = 'google-gsi-script';
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            if (window.google?.accounts?.id) {
                window.google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: handleGoogleResponse,
                    auto_select: false,
                    // In PWA / standalone mode use redirect so the callback survives
                    // the browser switching to the Google login page and back
                    ux_mode: USE_REDIRECT_MODE ? 'redirect' : 'popup',
                    ...(USE_REDIRECT_MODE ? { login_uri: GOOGLE_REDIRECT_URI } : {}),
                });
                setGsiReady(true);
            }
        };
        document.head.appendChild(script);
    }, [handleGoogleResponse]);

    // On mobile: render Google's own button after GSI is ready (or when tab changes).
    // Using renderButton() instead of prompt() avoids the iOS Safari popup-blocking issue.
    // In PWA mode: use redirect ux_mode so the button triggers a redirect flow instead of popup.
    useEffect(() => {
        if (!gsiReady || !IS_MOBILE_DEVICE || !window.google?.accounts?.id) return;
        const container = document.getElementById('google-btn-mobile');
        if (!container) return;
        container.innerHTML = '';
        window.google.accounts.id.renderButton(container, {
            theme: 'outline',
            size: 'large',
            width: Math.min(window.innerWidth - 64, 400),
            text: tab === 0 ? 'signin_with' : 'signup_with',
            locale: lang,
            shape: 'rectangular',
            ...(USE_REDIRECT_MODE ? { ux_mode: 'redirect', login_uri: GOOGLE_REDIRECT_URI } : {}),
        });
    }, [gsiReady, tab, lang]);

    const triggerGoogleSignIn = () => {
        if (window.google?.accounts?.id) {
            window.google.accounts.id.prompt((notification) => {
                if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                    // Fallback: render button in a temporary container
                    const btnContainer = document.getElementById('google-btn-container');
                    if (btnContainer) {
                        btnContainer.innerHTML = '';
                        window.google.accounts.id.renderButton(btnContainer, {
                            theme: 'outline',
                            size: 'large',
                            width: '100%',
                            text: tab === 0 ? 'signin_with' : 'signup_with',
                            locale: lang,
                        });
                        // Auto-click the rendered button
                        setTimeout(() => {
                            const btn = btnContainer.querySelector('[role="button"]') || btnContainer.querySelector('div[style]');
                            if (btn) btn.click();
                        }, 300);
                    }
                }
            });
        } else {
            setError(t('login.errorGoogleUnavailable'));
        }
    };

    // ==================== FORGOT PASSWORD ====================
    const handleForgotOpen = () => {
        setForgotOpen(true);
        setForgotStep(0);
        setForgotEmail('');
        setForgotCode('');
        setForgotNewPass('');
        setForgotConfirm('');
        setForgotMessage('');
        setForgotError('');
    };

    const handleSendCode = async () => {
        if (!forgotEmail) return setForgotError(t('login.errorEnterEmail'));
        setForgotLoading(true);
        setForgotError('');
        try {
            const res = await api.post('/auth/forgot-password', { email: forgotEmail });
            setForgotMessage(res.data.message);
            setForgotStep(1);
        } catch (err) {
            setForgotError(err.response?.data?.message || t('login.errorSendCode'));
        } finally {
            setForgotLoading(false);
        }
    };

    const handleVerifyCode = () => {
        if (!forgotCode || forgotCode.length < 6) {
            setForgotError(t('login.errorEnterCode'));
            return;
        }
        setForgotError('');
        setForgotStep(2);
    };

    const handleResetPassword = async () => {
        if (forgotNewPass.length < 6) return setForgotError(t('login.errorMinPassword'));
        if (forgotNewPass !== forgotConfirm) return setForgotError(t('login.errorPasswordMatch'));
        setForgotLoading(true);
        setForgotError('');
        try {
            const res = await api.post('/auth/reset-password', {
                email: forgotEmail,
                code: forgotCode,
                newPassword: forgotNewPass,
            });
            setForgotMessage(res.data.message);
            setTimeout(() => setForgotOpen(false), 2000);
        } catch (err) {
            setForgotError(err.response?.data?.message || t('login.errorResetPassword'));
        } finally {
            setForgotLoading(false);
        }
    };

    // If already logged in, redirect
    if (user) {
        return <Navigate to={postAuthPath() || (user.role === 'user' ? '/webmail' : '/dashboard')} replace />;
    }

    return (
        <Box dir={isRtl ? 'rtl' : 'ltr'} sx={{ 
            minHeight: '100vh',
            '@supports (height: 100svh)': { minHeight: '100svh' },
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            background: isWhiteLabel && branding.loginBgColor
                ? branding.loginBgColor
                : isWhiteLabel && branding.primaryColor
                    ? `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.secondaryColor || branding.primaryColor} 100%)`
                    : 'linear-gradient(135deg, #1e3a5f 0%, #3B82F6 50%, #8B5CF6 100%)',
            py: 4,
        }}>
            <Container maxWidth="sm">
                <Card sx={{ width: '100%', boxShadow: 8, borderRadius: 3, overflow: 'visible' }}>
                    <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                        {/* Logo */}
                        <Box sx={{ mb: 3, textAlign: 'center' }}>
                            {isWhiteLabel && branding.logoUrl ? (
                                <img src={branding.logoUrl} alt={branding.companyName || ''} style={{ maxHeight: 64, maxWidth: '80%', marginBottom: 8 }} />
                            ) : (
                                <LionIcon sx={{ fontSize: 56, mb: 1 }} />
                            )}
                            <Typography variant="h4" component="h1" fontWeight="bold" color="primary" gutterBottom>
                                {isWhiteLabel && branding.companyName ? branding.companyName : 'Email Lion'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {isWhiteLabel && branding.loginTitle ? branding.loginTitle : t('login.subtitle')}
                            </Typography>
                            {wantDemo && (
                                <Alert severity="info" sx={{ mt: 2, textAlign: 'start' }}>
                                    אחרי התחברות נפתח חיבור לתיבת הדמו (demo@emaillion.net) — בלי הגדרות ספק.
                                </Alert>
                            )}
                            {safeReturn && (
                                <Alert severity="info" sx={{ mt: wantDemo ? 1 : 2, textAlign: 'start' }}>
                                    אחרי התחברות נחזיר אתכם להמשך התשלום / הדף המבוקש.
                                </Alert>
                            )}
                            {isWhiteLabel && branding.loginSubtitle && (
                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                                    {branding.loginSubtitle}
                                </Typography>
                            )}
                        </Box>

                        {/* Tabs: Login / Register (hide register on white label) */}
                        <Tabs
                            value={isWhiteLabel ? 0 : tab}
                            onChange={(_, v) => { if (!isWhiteLabel || v === 0) { setTab(v); setError(''); setSuccess(''); } }}
                            centered
                            sx={{
                                mb: 3,
                                '& .MuiTab-root': { fontWeight: 'bold', fontSize: '1rem', minHeight: 48 },
                                '& .MuiTabs-indicator': { height: 3, borderRadius: 2 },
                            }}
                        >
                            <Tab icon={<LoginIcon />} iconPosition="start" label={t('login.loginTab')} />
                            {!isWhiteLabel && <Tab icon={<PersonAddIcon />} iconPosition="start" label={t('login.registerTab')} />}
                        </Tabs>

                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                        {/* ==================== LOGIN FORM ==================== */}
                        {tab === 0 && (
                            <form onSubmit={handleLogin}>
                                <TextField
                                    fullWidth label={t('login.emailLabel')} type="email" margin="normal"
                                    value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
                                    required autoFocus sx={{ direction: 'ltr' }}
                                />
                                <TextField
                                    fullWidth label={t('login.passwordLabel')} type={showPassword ? 'text' : 'password'} margin="normal"
                                    value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
                                    required sx={{ direction: 'ltr' }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small" aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}>
                                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {show2FA && (
                                    <TextField
                                        fullWidth label={t('login.twoFactorLabel')} margin="normal"
                                        value={twoFactorCode} onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        required autoFocus placeholder="000000"
                                        inputProps={{ maxLength: 6, style: { textAlign: 'center', letterSpacing: 8, fontSize: '1.3rem' } }}
                                        sx={{ direction: 'ltr' }}
                                        helperText={t('login.twoFactorHelper')}
                                    />
                                )}
                                <Box sx={{ mt: 1, mb: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={rememberMe && !rememberForever}
                                                onChange={(e) => {
                                                    setRememberMe(e.target.checked);
                                                    if (e.target.checked) setRememberForever(false);
                                                }}
                                                size="small"
                                                color="primary"
                                                disabled={rememberForever}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" color="text.secondary">
                                                {t('login.rememberMe30')}
                                            </Typography>
                                        }
                                        sx={{ mb: 0 }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={rememberForever}
                                                onChange={(e) => {
                                                    setRememberForever(e.target.checked);
                                                    // "Forever" is a stronger version of remember-me, so
                                                    // implicitly tick that too for visual clarity.
                                                    if (e.target.checked) setRememberMe(true);
                                                }}
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" component="span">
                                                    {t('login.keepLoggedInForever')}
                                                </Typography>
                                                <Typography variant="caption" color="text.disabled" display="block" sx={{ ml: 0, mt: 0.25 }}>
                                                    {t('login.keepLoggedInHint')}
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{ mt: 0.5, mb: 0, alignItems: 'flex-start' }}
                                    />
                                </Box>
                                <Button 
                                    type="submit" fullWidth variant="contained" size="large" disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                                    sx={{ mt: 3, mb: 1, py: 1.3, fontWeight: 'bold', fontSize: '1rem' }}
                                >
                                    {show2FA ? t('login.verifyAndLogin') : t('login.loginButton')}
                                </Button>

                                <Button
                                    fullWidth variant="text" size="small"
                                    startIcon={<LockResetIcon />}
                                    onClick={handleForgotOpen}
                                    sx={{ mb: 1, color: 'text.secondary', fontSize: '0.85rem' }}
                                >
                                    {t('login.forgotPassword')}
                                </Button>
                            </form>
                        )}

                        {/* ==================== REGISTER FORM ==================== */}
                        {tab === 1 && (
                            <form onSubmit={handleRegister}>
                                <TextField
                                    fullWidth label={t('login.fullName')} margin="dense"
                                    value={regName} onChange={(e) => setRegName(e.target.value)}
                                    required autoFocus
                                />
                                <TextField
                                    fullWidth label={t('login.emailRequired')} type="email" margin="dense"
                                    value={regEmail} onChange={(e) => setRegEmail(e.target.value)}
                                    required sx={{ direction: 'ltr' }}
                                />
                                <TextField
                                    fullWidth label={t('login.passwordRequired')} type={showPassword ? 'text' : 'password'} margin="dense"
                                    value={regPassword} onChange={(e) => setRegPassword(e.target.value)}
                                    required sx={{ direction: 'ltr' }}
                                    helperText={t('login.minChars')}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small" aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}>
                                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    fullWidth label={t('login.confirmPassword')} type="password" margin="dense"
                                    value={regConfirm} onChange={(e) => setRegConfirm(e.target.value)}
                                    required sx={{ direction: 'ltr' }}
                                    error={regConfirm.length > 0 && regPassword !== regConfirm}
                                    helperText={regConfirm.length > 0 && regPassword !== regConfirm ? t('login.passwordsMismatch') : ''}
                                />
                                <TextField
                                    fullWidth label={t('login.companyName')} margin="dense"
                                    value={regCompany} onChange={(e) => setRegCompany(e.target.value)}
                                />
                                <TextField
                                    fullWidth label={t('login.phone')} margin="dense"
                                    value={regPhone} onChange={(e) => setRegPhone(e.target.value)}
                                    required sx={{ direction: 'ltr' }}
                                    placeholder="050-1234567"
                                    helperText={t('login.phoneHelper')}
                                />

                                <Button 
                                    type="submit" fullWidth variant="contained" size="large" disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />}
                                    sx={{ mt: 2, mb: 1, py: 1.3, fontWeight: 'bold', fontSize: '1rem', bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}
                                >
                                    {loading ? t('login.registering') : t('login.registerFree')}
                                </Button>

                                <Box sx={{ textAlign: 'center', mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        <CheckCircleIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5, color: '#10b981' }} />
                                        {t('login.freePlan')}
                                    </Typography>
                                </Box>
                            </form>
                        )}

                        {/* ==================== GOOGLE SIGN-IN (hidden on white label — redirect URI mismatch) ==================== */}
                        {GOOGLE_CLIENT_ID && !isWhiteLabel && (
                            <>
                                <Divider sx={{ my: 2 }}>
                                    <Typography variant="caption" color="text.secondary">{t('common.or')}</Typography>
                                </Divider>

                                {IS_MOBILE_DEVICE ? (
                                    /* Mobile / iOS Safari:
                                       Use Google's own rendered button so the click is a direct
                                       user gesture — this bypasses Safari's popup blocker which
                                       causes the white-page issue when using prompt(). */
                                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', minHeight: 46 }}>
                                        {gsiReady
                                            ? <div id="google-btn-mobile" style={{ width: '100%' }} />
                                            : <Button fullWidth variant="outlined" disabled
                                                startIcon={<CircularProgress size={18} />}
                                                sx={{ py: 1.3 }}>
                                                {t('common.loading')}
                                            </Button>
                                        }
                                    </Box>
                                ) : (
                                    /* Desktop: custom MUI button + prompt() with hidden fallback */
                                    <>
                                        <Button
                                            fullWidth variant="outlined" size="large"
                                            onClick={triggerGoogleSignIn}
                                            disabled={loading}
                                            startIcon={<GoogleIcon />}
                                            sx={{
                                                py: 1.3, fontWeight: 'bold', fontSize: '0.95rem',
                                                borderColor: '#dadce0', color: '#3c4043',
                                                '&:hover': { borderColor: '#1a73e8', bgcolor: '#f8f9fa' },
                                            }}
                                        >
                                            {tab === 0 ? t('login.googleLogin') : t('login.googleRegister')}
                                        </Button>
                                        <div id="google-btn-container" style={{ display: 'none' }}></div>
                                    </>
                                )}
                            </>
                        )}
                        
                        <Divider sx={{ my: 2 }} />
                        
                        {/* Terms notice */}
                        {tab === 1 && (
                            <Typography variant="caption" color="text.secondary" textAlign="center" display="block" sx={{ mb: 1 }}>
                                {t('login.termsAgree')}
                                <Typography component="a" variant="caption" href="/terms" sx={{ color: 'primary.main', mx: 0.5 }}>{t('login.termsOfService')}</Typography>
                                {t('login.and')}
                                <Typography component="a" variant="caption" href="/privacy" sx={{ color: 'primary.main', mx: 0.5 }}>{t('login.privacyPolicy')}</Typography>
                            </Typography>
                        )}

                        {!isWhiteLabel && (
                            <Button 
                                fullWidth variant="text" startIcon={<ArrowBackIcon />}
                                onClick={() => navigate('/')}
                                sx={{ color: 'text.secondary' }}
                            >
                                {t('login.backToSite')}
                            </Button>
                        )}
                    </CardContent>
                </Card>
            </Container>

            {/* ============ Forgot Password Dialog ============ */}
            <Dialog open={forgotOpen} onClose={() => setForgotOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                    <LockResetIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1, display: 'block', mx: 'auto' }} />
                    {t('login.resetPassword')}
                </DialogTitle>
                <DialogContent>
                    <Stepper activeStep={forgotStep} alternativeLabel sx={{ mb: 3 }}>
                        <Step><StepLabel>{t('login.stepEnterEmail')}</StepLabel></Step>
                        <Step><StepLabel>{t('login.stepVerifyCode')}</StepLabel></Step>
                        <Step><StepLabel>{t('login.stepNewPassword')}</StepLabel></Step>
                    </Stepper>

                    {forgotMessage && <Alert severity="success" sx={{ mb: 2 }}>{forgotMessage}</Alert>}
                    {forgotError && <Alert severity="error" sx={{ mb: 2 }}>{forgotError}</Alert>}

                    {forgotStep === 0 && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t('login.resetEmailPrompt')}
                            </Typography>
                            <TextField
                                fullWidth label={t('login.emailAddress')} type="email"
                                value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                                sx={{ direction: 'ltr' }} autoFocus
                            />
                        </Box>
                    )}
                    {forgotStep === 1 && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                <>{t('login.codeSentTo')}<strong>{forgotEmail}</strong>{t('login.enterCode')}</>
                            </Typography>
                            <TextField
                                fullWidth label={t('login.verificationCode')} value={forgotCode}
                                onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: 24, letterSpacing: 8 } }}
                                autoFocus
                            />
                        </Box>
                    )}
                    {forgotStep === 2 && (
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t('login.newPasswordPrompt')}
                            </Typography>
                            <TextField
                                fullWidth label={t('login.newPassword')} type="password"
                                value={forgotNewPass} onChange={(e) => setForgotNewPass(e.target.value)}
                                sx={{ mb: 2, direction: 'ltr' }} autoFocus
                            />
                            <TextField
                                fullWidth label={t('login.confirmPasswordLabel')} type="password"
                                value={forgotConfirm} onChange={(e) => setForgotConfirm(e.target.value)}
                                sx={{ direction: 'ltr' }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setForgotOpen(false)}>{t('common.cancel')}</Button>
                    {forgotStep === 0 && (
                        <Button variant="contained" onClick={handleSendCode} disabled={forgotLoading}>
                            {forgotLoading ? <CircularProgress size={22} /> : t('login.sendCode')}
                        </Button>
                    )}
                    {forgotStep === 1 && (
                        <Button variant="contained" onClick={handleVerifyCode}>{t('login.verifyCode')}</Button>
                    )}
                    {forgotStep === 2 && (
                        <Button variant="contained" onClick={handleResetPassword} disabled={forgotLoading}>
                            {forgotLoading ? <CircularProgress size={22} /> : t('login.resetPasswordBtn')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <PoweredByBadge sx={{ mt: 2, pb: 2 }} />
        </Box>
    );
};

export default LoginPage;
